import { type FindRules, findsField, type Load } from "../finds";
import { type AxialCoord, hexKey, hexNeighbors } from "../hex";
import { enemyWithin, findHex, unitsOf } from "../run/board";
import { seatAt, seatUnit, soldierSeats } from "../run/seats";
import type { FactionId, FactionTable, RunState, SeatId, SeatState, Unit } from "../types/state";
import { isVeteran } from "../units/combat";
import { isBroken } from "../units/morale";
import { replaceUnit } from "../units/unit";
import { comradeBeside } from "./company";
import { isCityHex, isWorking } from "./work";

/**
 * The soldier's finds (ADR-0014 M2): the hidden field, reading a hex and taking its load (tick step
 * 1e); the purse, renown and debt readers.
 */

const NO_FINDS: ReadonlyMap<string, number> = new Map();

/**
 * The hidden layer of a run, cached the way the topology is: a pure function of the seed, the
 * land and the rules, so the cache holds nothing the run depends on. Keyed by the first coord
 * object, which a run keeps through every change of its hexes.
 */
const FINDS_BY_FIRST_COORD = new WeakMap<
  AxialCoord,
  {
    readonly seed: number;
    readonly salt: number | undefined;
    readonly rules: FindRules;
    readonly hexCount: number;
    readonly field: ReadonlyMap<string, number>;
  }
>();

/**
 * Everything the land held at the start of the run (M2), by hex key: what the sim and the
 * tests read. A screen reads `findAt` on the hexes its kingdom has revealed and nothing else.
 */
export function findsOf(state: RunState): ReadonlyMap<string, number> {
  const rules = state.rules.finds;
  const first = state.hexes[0]?.coord;
  if (!rules || !first) {
    return NO_FINDS;
  }
  const cached = FINDS_BY_FIRST_COORD.get(first);
  if (
    cached &&
    cached.seed === state.seed &&
    cached.salt === state.findsSalt &&
    cached.rules === rules &&
    cached.hexCount === state.hexes.length
  ) {
    return cached.field;
  }
  const field = findsField((state.seed ^ (state.findsSalt ?? 0)) >>> 0, state.hexes, rules);
  FINDS_BY_FIRST_COORD.set(first, {
    seed: state.seed,
    salt: state.findsSalt,
    rules,
    hexCount: state.hexes.length,
    field,
  });
  return field;
}

/** The worth of the find lying on the hex now: 0 when the land holds none there or it has been taken. */
export function findAt(state: RunState, coord: AxialCoord): number {
  const key = hexKey(coord);
  const value = findsOf(state).get(key) ?? 0;
  return value > 0 && state.spent?.includes(key) ? 0 : value;
}

/** Whether the kingdom's soldiers have prospected the hex: only then do its seats' screens show what lies there. */
export function isRevealed(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  return state.revealed?.[faction]?.includes(hexKey(coord)) ?? false;
}

/** The money of a soldier seat; 0 without one. */
export function purseOf(seat: SeatState | undefined): number {
  return seat?.purse ?? 0;
}

/** The renown of a soldier seat (C1); 0 without one. */
export function renownOf(seat: SeatState | undefined): number {
  return seat?.renown ?? 0;
}

/** What the King owes a soldier seat (C1); 0 without one. */
export function owedOf(seat: SeatState | undefined): number {
  return seat?.owed ?? 0;
}

/**
 * How long a soldier stands to read its hex (M2): `finds.prospectTicks`, or the company's with
 * a comrade beside it (C2). 0 without the finds.
 */
export function prospectWait(state: RunState, unit: Unit): number {
  const { finds, company } = state.rules;
  if (!finds) {
    return 0;
  }
  return company && comradeBeside(state, unit) ? company.prospectTicks : finds.prospectTicks;
}

/** Whether the unit has stood long enough to read the land under it: idle or at its work (CC5) outside a city, unbroken, its wait over. */
function standsToRead(state: RunState, unit: Unit): boolean {
  return (
    (unit.destination === undefined || (isWorking(state, unit) && !isCityHex(state, unit.coord))) &&
    !isBroken(state, unit) &&
    state.tick - unit.movedTick >= prospectWait(state, unit)
  );
}

/**
 * What a soldier that stands to read would add to `known`: its hex, a veteran's the six around it
 * too; nothing under an enemy's eyes (one beside it, the heal rule's condition).
 */
function readBy(state: RunState, unit: Unit, known: readonly string[]): string[] {
  const around = isVeteran(state, unit) ? [unit.coord, ...hexNeighbors(unit.coord)] : [unit.coord];
  const learned = around
    .filter((coord) => findHex(state, coord) !== undefined)
    .map(hexKey)
    .filter((key) => !known.includes(key));
  return learned.length === 0 || enemyWithin(state, unit.owner, unit.coord, 1) ? [] : learned;
}

/**
 * Step 1e (M2), seat by seat in id order: a soldier seat's unit prospects. Idle, unbroken, with no
 * enemy beside it (the heal rule's condition) and `finds.prospectTicks` after its last step, it
 * learns what its hex holds, a veteran the hexes around it too; empty hands take the find under
 * its feet and the hex is spent for everyone. A soldier on the march learns nothing of the land it
 * crosses, save a seat's at its work (CC5), which stands on its road to read it.
 * One map (C2; K0: the kingdom's): every soldier of the seat's kingdom reads by the same rule and
 * what it reads is read for the kingdom; only the seat's takes a find. Side by side the wait is
 * the company's.
 */
export function prospect(state: RunState): RunState {
  if (!state.rules.finds) {
    return state;
  }
  let next = state;
  for (const { id } of soldierSeats(state)) {
    next = prospectFor(next, id);
  }
  return next;
}

function prospectFor(state: RunState, seatId: SeatId): RunState {
  const rules = state.rules.finds;
  const seat = seatAt(state, seatId);
  const unit = seatUnit(state, seat);
  if (!rules || !seat) {
    return state;
  }
  const comrades = state.rules.company
    ? unitsOf(state, seat.faction).filter((other) => other.id !== unit?.id)
    : [];
  const reads = unit !== undefined && standsToRead(state, unit);
  if (!reads && comrades.length === 0) {
    return state;
  }
  const table = state.revealed ?? {};
  const known = table[seat.faction] ?? [];
  let learned: string[] = unit && reads ? readBy(state, unit, known) : [];
  for (const comrade of comrades) {
    if (standsToRead(state, comrade)) {
      learned = [...learned, ...readBy(state, comrade, [...known, ...learned])];
    }
  }
  // VC: a leader carries no load; it leads or it hauls.
  const value =
    unit && reads && !unit.carry && unit.leadsSince === undefined ? findAt(state, unit.coord) : 0;
  const takes = unit !== undefined && value > 0 && !enemyWithin(state, unit.owner, unit.coord, 1);
  if (learned.length === 0 && !takes) {
    return state;
  }
  const revealed: FactionTable<readonly string[]> =
    learned.length === 0 ? table : { ...table, [seat.faction]: [...known, ...learned] };
  if (!unit || !takes) {
    return { ...state, revealed };
  }
  const load: Load = { value, from: unit.coord };
  return {
    ...replaceUnit(state, unit, { ...unit, carry: load }),
    revealed,
    spent: [...(state.spent ?? []), hexKey(unit.coord)],
  };
}
